import React from "react";
import { Link } from "react-router-dom";
import { Sparkle, Bug, Wrench, ShieldCheck, Pencil } from "@phosphor-icons/react";

const ENTRIES = [
  {
    date: "Jul 28, 2026",
    version: "1.2.0",
    items: [
      { type: "feature", text: "Commercialization Center — a single hub per product that pulls together your label, spec sheet, costing, and readiness status in one place, so you always know where a product stands on its way to market." },
      { type: "feature", text: "Commercial Readiness Check with status tiers — every product now gets a clear readiness rating (from early R&D to co-packer ready) with a checklist of exactly what's missing before you can hand it off." },
      { type: "feature", text: "One-click Commercialization Package PDF — export a polished, co-manufacturer-ready package (label, spec sheet, ingredient statement, and key product details) in a single click." },
      { type: "feature", text: "Courtney's Insights — plain-language guidance on your formulation as you build, including acidity risk checks (pH and citric acid) that flag when a sauce or dressing may need an acidified-foods review before production." },
      { type: "feature", text: "Label print presets — ready-made print layouts for bottle and jar wraps using the FDA linear small-package format, so short labels come out right the first time." },
      { type: "feature", text: "Smarter net quantity units — the net quantity field now suggests the right units for your product type (fl oz for sauces, oz/g for dry blends, and so on), keeping labels consistent with FDA convention." },
    ],
  },
  {
    date: "Feb 6, 2026",
    version: "0.9.4",
    items: [
      { type: "feature", text: "Microbiological spec defaults — auto-fills industry-standard targets (TPC, Y&M, Coliforms, E. coli, Salmonella, Listeria, S. aureus) for dry seasoning blends, with a mandatory 'must be tested by accredited lab' disclaimer." },
      { type: "feature", text: "Allergen formatting — Contains: line now renders allergens in ALL CAPS app-wide (Contains: WHEAT, SOY.) per FDA convention." },
      { type: "tweak",  text: "Spec Sheet section header renamed Allergen Statement → Ingredient Statement." },
      { type: "trust",  text: "Added ™ to brand-name uses across the app (Top nav, Coming Soon, Login, Register, Terms, print footers) — establishes common-law trademark rights ahead of USPTO filing." },
      { type: "feature", text: "Trademark Filing Prep Sheet — downloadable USPTO TEAS Plus walkthrough in Settings → Legal Toolkit." },
      { type: "feature", text: "Public Trust page (/trust) and Changelog page (/changelog)." },
    ],
  },
  {
    date: "Feb 4, 2026",
    version: "0.9.3",
    items: [
      { type: "feature", text: "Co-Packer Spec Sheet — full generator with identity, sensory profile, physical/chemical (mesh, density, moisture, salt content with ASTA tolerance bands), shelf-life logic, packaging defaults, ingredient breakdown, and cost summary." },
      { type: "feature", text: "Copyright deposit .txt files — USCO-ready exports for Software and Content/Database registrations, downloadable from Settings." },
      { type: "trust",  text: "Robots.txt blocks search-engine indexing of the app." },
    ],
  },
  {
    date: "Feb 1, 2026",
    version: "0.9.2",
    items: [
      { type: "feature", text: "Formulation Intelligence — 50+ ingredient usage limits encoded with reasoning (Spice House Pro Council, FEMA GRAS, ASTA, FDA 21 CFR), mode-aware (Retail vs Chef)." },
      { type: "feature", text: "Application Calculator — protein × weight × intensity → grams seasoning + sodium delivery per serving (12 protein types)." },
      { type: "feature", text: "Ingredient Intelligence drawer — flavor profile, typical %, upper limit, pairs-with, technical notes (~25 deeply-curated ingredients)." },
      { type: "feature", text: 'AI "Suggest a fix" button on formulation warnings — Claude Sonnet 4.5 reformulation advice grounded in our internal limits.' },
      { type: "feature", text: "Retail / Chef mode toggle in Formulation Builder header — drives flag thresholds." },
    ],
  },
  {
    date: "Jan 28, 2026",
    version: "0.9.1",
    items: [
      { type: "feature", text: "Cost & Pricing card — batch COGS, cost/lb, cost/oz, cost/serving, retail units per batch, markup %, suggested retail, gross margin." },
      { type: "feature", text: "Default supplier prices — 100% pricing coverage (91 industry-default prices + per-user overrides preserved)." },
      { type: "feature", text: "(Processing Aid) declarations on Silicon Dioxide, Calcium Silicate, Calcium Stearate, Soybean Oil, Canola Oil." },
      { type: "fix",     text: "Auth — extended JWT to 30 days and mirrored token to localStorage to fix Chrome/Edge cookie drops on preview URLs." },
      { type: "fix",     text: "Print — replaced unreliable CSS @media print with srcdoc iframe injection for Formulation and Spec sheets." },
      { type: "fix",     text: "Cache-control meta tags in index.html to prevent stale UI after deploys." },
    ],
  },
  {
    date: "Jan 20, 2026",
    version: "0.9.0",
    items: [
      { type: "feature", text: "Initial private release — 107 seeded ingredients (herbs, spices, yeast extracts, natural flavors, fillers)." },
      { type: "feature", text: "FDA-rounded nutrition calculator (21 CFR 101.9) with vertical label layout in Helvetica." },
      { type: "feature", text: "Formulation CRUD with per-user isolation, SKU / version / status (R&D / Pilot / Production / Archived), version duplication." },
      { type: "feature", text: "Live FDA label preview, batch scaling (100g / 1lb / 5lb / 10lb), allergen badges." },
      { type: "feature", text: "Legal Claim Validator (21 CFR 101.54 / 60 / 61 / 62)." },
      { type: "feature", text: "AI compliance chat with session persistence." },
      { type: "feature", text: "Terms of Service, Privacy Policy, Business Profile, Coming Soon landing." },
    ],
  },
];

export default function Changelog() {
  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white/60 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="changelog-home-link">
            <div className="h-8 w-8 rounded-md bg-emerald-800 text-white grid place-items-center font-bold">A</div>
            <div className="leading-none">
              <div className="font-bold text-lg tracking-tight">Alaine's Bench<sup className="text-[0.5em] font-semibold ml-0.5 align-super">™</sup></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-0.5">Changelog</div>
            </div>
          </Link>
          <Link to="/login" className="text-sm text-stone-500 hover:text-emerald-800" data-testid="changelog-login-link">
            Sign in
          </Link>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-800 font-bold mb-3">Changelog</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">
          Every release. <span className="text-emerald-800">In the open.</span>
        </h1>
        <p className="mt-5 text-stone-600 text-lg leading-relaxed">
          What we shipped, when, and why. If you want to see
          something next, <Link to="/contact" className="text-emerald-800 underline">send us a message</Link>.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="space-y-10">
          {ENTRIES.map((entry) => (
            <article key={entry.version} className="relative" data-testid={`changelog-entry-${entry.version}`}>
              <div className="flex items-baseline gap-3 pb-3 border-b border-stone-200">
                <span className="font-bold text-lg text-stone-900">{entry.version}</span>
                <span className="text-stone-500 text-sm">·</span>
                <span className="text-sm text-stone-500">{entry.date}</span>
              </div>
              <ul className="mt-4 space-y-3">
                {entry.items.map((it, ix) => (
                  <li key={ix} className="flex items-start gap-3">
                    <Tag type={it.type} />
                    <span className="text-stone-700 text-[15px] leading-relaxed">{it.text}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <footer className="border-t border-stone-200 bg-white py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-stone-500 flex justify-between flex-wrap gap-4">
          <div>
            &copy; {new Date().getFullYear()} <b>Shirleys Daughter, LLC</b> · Alaine's Bench™
          </div>
          <div className="flex gap-4">
            <Link to="/trust" className="hover:text-emerald-800" data-testid="changelog-to-trust">Trust</Link>
            <Link to="/terms" className="hover:text-emerald-800">Terms</Link>
            <Link to="/privacy" className="hover:text-emerald-800">Privacy</Link>
            <Link to="/" className="hover:text-emerald-800">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Tag({ type }) {
  const cfg = {
    feature: { icon: <Sparkle size={12} weight="fill" />, label: "NEW",     bg: "bg-emerald-100", color: "text-emerald-800" },
    fix:     { icon: <Bug size={12} weight="fill" />,     label: "FIX",     bg: "bg-amber-100",   color: "text-amber-800" },
    tweak:   { icon: <Pencil size={12} weight="fill" />,  label: "TWEAK",   bg: "bg-stone-200",   color: "text-stone-700" },
    trust:   { icon: <ShieldCheck size={12} weight="fill" />, label: "TRUST", bg: "bg-blue-100",  color: "text-blue-800" },
    chore:   { icon: <Wrench size={12} weight="fill" />,  label: "CHORE",   bg: "bg-stone-100",   color: "text-stone-600" },
  }[type] || { icon: null, label: type.toUpperCase(), bg: "bg-stone-100", color: "text-stone-600" };
  return (
    <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded mt-1 ${cfg.bg} ${cfg.color} uppercase tracking-wider`}>
      {cfg.icon}{cfg.label}
    </span>
  );
}
